import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../../axios/axiosInstance";
import { getDateFromISO } from "../../../utils/dateFunction";
import DatePicker from "../../../components/date-picker/DatePicker";
import { hasPermissionSync } from "../../../utils/get-user-permissions";

interface Prop {
  onClose: () => void;
  id?: any;
  requestData?: any;
  requestId?: any;
}

const vehicleTypes = [
  { value: "car", label: "Car" },
  { value: "bike", label: "Bike" },
  { value: "truck", label: "Truck" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "bus", label: "Bus" },
  { value: "van", label: "Van" },
  { value: "other", label: "Other" },
];

const UserCreateForm: React.FC<Prop> = ({
  onClose,
  id,
  requestData,
  requestId,
}) => {
  const role: any = localStorage.getItem("role");
  const [companies, setCompanies] = useState<any>([]);
  const [formData, setFormData] = useState<any>();
  const [initialFormData, setInitialFormData] = useState<any>(null);
  const [initialMultiple, setInitialMultiple] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any>([]);
  const [multiple, setMultiple] = useState<any[]>([]);
  const [imageFile, setImageFile] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [avatarError, setAvatarError] = useState(false);
  const [memberTypes, setMemberTypes] = useState<Array<{ label: string; value: string }>>([]);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      handleChage("is_change", "true");
    }
  };
  const [zones, setZones] = useState<any[]>([]);
  
  // Location states
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [taluks, setTaluks] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  
  // Selected location IDs
  const [selectedCountryId, setSelectedCountryId] = useState<number | null>(null);
  const [selectedStateId, setSelectedStateId] = useState<number | null>(null);
  const [selectedDistrictId, setSelectedDistrictId] = useState<number | null>(null);
  const [selectedTalukId, setSelectedTalukId] = useState<number | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<number | null>(null);
  const [initialCityId, setInitialCityId] = useState<number | null>(null);
  const [useFilter, setUseFilter] = useState<boolean>(false);

  const getCompnay = async () => {
    try {
      const resp: any = await axiosInstance.get("/api/company");
      if (resp.status === 200) {
        setCompanies(resp.data?.data);
      }
    } catch (error: any) {
      console.error(error);
      toast.error("Error while getting");
    }
  };

  const getZones = async () => {
    try {
      // Check if user is Super Admin or has admin permission
      const isSuperAdmin = localStorage.getItem("isSuperAdmin") === "true" || Number(role) === 0;
      const hasAdminPermission = hasPermissionSync("admin");
      const canViewAllZones = isSuperAdmin || hasAdminPermission;
      
      if (canViewAllZones) {
        // Super Admin and users with admin permission see all zones
        const resp: any = await axiosInstance.get("/api/zone/all");
        if (resp.status === 200 && resp.data?.status === "ok") {
          setZones(resp.data?.data || []);
        }
      } else {
        // Check if user is Zone Admin by calling /api/zone/my-zones
        // If this returns zones, user is a Zone Admin
        try {
          const resp: any = await axiosInstance.get("/api/zone/my-zones");
          if (resp.status === 200 && resp.data?.status === "ok" && resp.data.data && resp.data.data.length > 0) {
            // User is a Zone Admin - show only their zones
            setZones(resp.data?.data || []);
          } else {
            // User is not a Zone Admin - show empty zones list (they won't see any members anyway)
            setZones([]);
          }
        } catch (error) {
          console.error("Failed to fetch zone admin zones:", error);
          // If endpoint fails, user is likely not a Zone Admin
          setZones([]);
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch zones");
    }
  };

  const fetchMemberTypes = async () => {
    try {
      const resp: any = await axiosInstance.get("/api/member-type");
      if (resp.status === 200 && resp.data?.status === "ok") {
        const types = resp.data.data.map((type: any) => ({
          label: type.label,
          value: type.value,
        }));
        setMemberTypes(types);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch member types");
      // Fallback to empty array if API fails
      setMemberTypes([]);
    }
  };

  // Location fetch functions
  const fetchCountries = async () => {
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>('/api/country');
      if (res.status === 200) {
        setCountries(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchStates = async (countryId: number | null) => {
    if (!countryId) {
      setStates([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/state?country_id=${countryId}`);
      if (res.status === 200) {
        setStates(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch states:', error);
      setStates([]);
    }
  };

  const fetchDistricts = async (stateId: number | null) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/district?state_id=${stateId}`);
      if (res.status === 200) {
        setDistricts(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch districts:', error);
      setDistricts([]);
    }
  };

  const fetchTaluks = async (districtId: number | null) => {
    if (!districtId) {
      setTaluks([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/taluk?district_id=${districtId}`);
      if (res.status === 200) {
        setTaluks(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch taluks:', error);
      setTaluks([]);
    }
  };

  const fetchAllCities = async () => {
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/city`);
      if (res.status === 200) {
        setCities(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch cities:', error);
      setCities([]);
    }
  };

  const fetchCities = async (talukId: number | null) => {
    if (!talukId) {
      setCities([]);
      return;
    }
    try {
      const res = await axiosInstance.get<{ status: string; data: any[] }>(`/api/city?taluk_id=${talukId}`);
      if (res.status === 200) {
        setCities(res.data.data || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch cities:', error);
      setCities([]);
    }
  };

  const handleGet = async (id: any) => {
    try {
      const resp: any = await axiosInstance.get(`/api/user/${id}`);
      if (resp.status === 200) {
        console.log(
          "call hitt==================",
          resp?.data?.data?.image_path
        );

        const cityData = resp?.data?.data?.city;
        // date_of_birth is directly on data object, not under profile
        const dateOfBirth = resp?.data?.data?.date_of_birth || resp?.data?.data?.profile?.date_of_birth;
        const dateOfDeath = resp?.data?.data?.date_of_death || resp?.data?.data?.profile?.date_of_death;
        
        // Helper function to convert to boolean
        const toBoolean = (value: any): boolean => {
          if (value === null || value === undefined) return false;
          if (typeof value === 'boolean') return value;
          if (typeof value === 'string') {
            return value.toLowerCase() === 'true' || value === '1';
          }
          return Boolean(value);
        };
        
        // Helper function to format date
        const formatDate = (dateValue: any): string => {
          if (!dateValue) return "";
          if (typeof dateValue !== 'string') return "";
          // If already in YYYY-MM-DD format, return as is
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
          // If it's an ISO date string, convert it
          if (dateValue.includes('T') || dateValue.includes(' ')) {
            return getDateFromISO(dateValue);
          }
          // Try to parse and format
          try {
            return getDateFromISO(dateValue);
          } catch {
            return dateValue;
          }
        };
        
        const data = {
          id: resp.data?.data?.id,
          name: resp.data?.data?.name,
          phone: resp.data?.data?.phone,
          is_whatsapp_enabled: resp.data?.data?.is_whatsapp_enabled,
          address: resp.data?.data?.address,
          is_visitor: resp?.data?.data?.is_visitor,
          is_leader: toBoolean(resp?.data?.data?.is_leader),
          is_family_head: toBoolean(resp?.data?.data?.is_family_head),
          zone_id: resp.data?.data?.zone?.id,
          member_type: resp?.data?.data?.member_type,
          date_of_birth: formatDate(dateOfBirth),
          first_communion_date: formatDate(resp?.data?.data?.first_communion_date),
          baptism_date: formatDate(resp?.data?.data?.baptism_date),
          date_of_death: formatDate(dateOfDeath),
          is_deceased: Boolean(dateOfDeath),
          gender: resp?.data?.data?.gender,
          profile_id: resp?.data?.data?.profile?.id,
          city_id: cityData?.id || null,
        };
        
        console.log("Loaded form data:", data);
        console.log("Date of birth raw:", dateOfBirth);
        console.log("Is family head raw:", resp?.data?.data?.is_family_head);
        console.log("Full response:", resp?.data?.data);

        // Store initial city_id for change detection
        setInitialCityId(cityData?.id || null);

        // Set location hierarchy if city exists
        if (cityData) {
          const countryId = cityData.taluk?.district?.state?.country?.id;
          const stateId = cityData.taluk?.district?.state?.id;
          const districtId = cityData.taluk?.district?.id;
          const talukId = cityData.taluk?.id;
          
          // Initialize location hierarchy sequentially
          const initLocation = async () => {
            if (countryId) {
              setSelectedCountryId(countryId);
              await fetchStates(countryId);
              
              if (stateId) {
                setSelectedStateId(stateId);
                await fetchDistricts(stateId);
                
                if (districtId) {
                  setSelectedDistrictId(districtId);
                  await fetchTaluks(districtId);
                  
                  if (talukId) {
                    setSelectedTalukId(talukId);
                    await fetchCities(talukId);
                    
                    setSelectedCityId(cityData.id);
                  }
                }
              }
            } else {
              setSelectedCityId(cityData.id);
            }
          };
          
          initLocation();
        }

        console.log(data);
        // APARTMENT FUNCTIONALITY DISABLED
        // setMultiple(
        //   resp?.data?.data?.appartments?.map((item: any, index: any) => {
        //     return item?.id;
        //   })
        // );
        console.log("===========", data);

        setFormData(data);
        setPreviewUrl(`${process.env.API_URI}${resp?.data?.data?.image_path}`);
        setInitialFormData(data);
        // APARTMENT FUNCTIONALITY DISABLED
        // setInitialMultiple(
        //   resp?.data?.data?.appartments?.map((item: any, index: any) => {
        //     return item?.id;
        //   })
        // );
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.body);
    }
  };
  console.log(previewUrl);

  const handleChage = (field: string, value: string | boolean) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    const ogFormData = new FormData();

    // When editing (not approving), block submit if nothing changed
    if (id && !requestId) {
      const noFieldChanges =
        JSON.stringify(formData) === JSON.stringify(initialFormData);
      const noCityChanges = selectedCityId === initialCityId;

      if (noFieldChanges && noCityChanges) {
        toast.info("No changes were made");
        return;
      }
    }

    if (requestData) {
      const noFieldChanges =
        JSON.stringify(formData) === JSON.stringify(initialFormData);
      // APARTMENT FUNCTIONALITY DISABLED
      // const noAptChanges =
      //   JSON.stringify(multiple) === JSON.stringify(initialMultiple);
      const noCityChanges = selectedCityId === initialCityId;

      // if (!noFieldChanges && noAptChanges && noCityChanges) {
      if (!noFieldChanges && noCityChanges) {
        ogFormData.append("is_admin_made_change", "true");
      }
    }

    const requiredFields = [
      { key: "name", message: "Name is required" },
      { key: "phone", message: "Phone is required" },
      { key: "address", message: "Address is required" },
      { key: "date_of_birth", message: "DOB is required" },
      { key: "gender", message: "Gender is required" },
    ];

    for (const field of requiredFields) {
      if (!formData?.[field.key]) {
        toast.error(field.message);
        return;
      }
    }

    // Special validation for member_type (should not be 0 or empty)
    if (!formData?.member_type || formData?.member_type === "0" || formData?.member_type === 0) {
      toast.error("Member type is required");
      return;
    }

    if (formData?.phone?.length !== 10) {
      toast.error("Invalid contact no");
      return;
    }

    if (formData?.reg_no && !formData?.type) {
      toast.error("Vehicle type is required");
      return;
    }

    if (id) {
      if (formData?.is_deceased) {
        ogFormData.append("is_deceased", formData?.is_deceased);
        if (formData?.date_of_death) {
          ogFormData.append("date_of_death", formData?.date_of_death);
        }
      } else {
        ogFormData.delete && ogFormData.delete("date_of_death");
      }
    }

    if (formData?.id) {
      ogFormData.append("id", formData?.id);
    }
    ogFormData.append("name", formData.name);
    ogFormData.append("phone", formData.phone);
    ogFormData.append("address", formData.address);
    ogFormData.append("member_type", formData.member_type);

    formData.is_leader && ogFormData.append("is_leader", formData.is_leader);
    formData.is_family_head && ogFormData.append("is_family_head", formData.is_family_head);
    formData.is_whatsapp_enabled &&
      ogFormData.append("is_whatsapp_enabled", formData.is_whatsapp_enabled);
    formData?.zone_id && ogFormData.append("zone_id", formData?.zone_id);
    ogFormData.append("gender", formData?.gender);
    ogFormData.append("date_of_birth", formData?.date_of_birth);
    ogFormData.append("baptism_date", formData?.baptism_date);
    ogFormData.append("first_communion_date", formData?.first_communion_date);

    formData?.profile_id &&
      ogFormData.append("profile_id", formData?.profile_id);

    ogFormData.append("requester", role);

    // APARTMENT FUNCTIONALITY DISABLED
    // multiple.forEach((id) => {
    //   ogFormData.append("apartment_ids[]", id);
    // });

    if (formData.reg_no) ogFormData.append("reg_no", formData.reg_no);
    if (formData.type) ogFormData.append("type", formData.type);
    
    // Handle city_id - send it if selected, or explicitly send empty string for updates to clear location
    if (selectedCityId) {
      ogFormData.append("city_id", selectedCityId.toString());
    } else if (id && formData?.city_id) {
      // If editing and had a city_id before but now cleared, send empty to remove location
      ogFormData.append("city_id", '');
    }
    if (imageFile) {
      ogFormData.append("file", imageFile);
    } else if (formData?.img_path || formData?.image_path) {
      ogFormData.append("image_path", formData.img_path || formData.image_path);
    }

    if (formData?.id) {
      handleUpdate(ogFormData);
    } else {
      handleCreate(ogFormData);
    }
  };

  // APARTMENT FUNCTIONALITY DISABLED
  // const handleChangeCompany = (event: any) => {
  //   const {
  //     target: { value },
  //   } = event;
  //   setMultiple(typeof value === "string" ? value.split(",") : value);
  // };

  const handleCreate = async (formData: any) => {
    try {
      // Check if user is Super Admin or has admin permission (both can execute directly)
      const isSuperAdmin = Number(role) === 0;
      const hasAdminPermission = hasPermissionSync("admin");
      const canExecuteDirectly = isSuperAdmin || hasAdminPermission;
      
      // When approving a request, execute directly without invocation
      // Otherwise, create a request for approval (unless user can execute directly)
      const url = requestId 
        ? `/api/user` 
        : `/api/user?invocation=${canExecuteDirectly ? 0 : role}`;
      
      const resp: any = await axiosInstance.post(
        url,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (resp.status === 200) {
        if (requestId) {
          handleUpdateStatus(requestId);
        }

        if (requestId) {
          toast.success("Request approved and user created successfully");
        } else if (canExecuteDirectly) {
          toast.success("User created successfully");
        } else {
          toast.success("Add user request sent to Super Admin");
        }
        onClose();
        getCompnay();
        setFormData(null);
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.body || error?.message);
    }
  };

  const handleUpdate = async (formDataPayload: any) => {
    console.log("formDataPayload", formDataPayload);
    
    // Check if user is Super Admin or has admin permission (both can execute directly)
    const isSuperAdmin = Number(role) === 0;
    const hasAdminPermission = hasPermissionSync("admin");
    const canExecuteDirectly = isSuperAdmin || hasAdminPermission;
    
    // Check if the user being updated is a Super Admin
    // Only Super Admins and users with admin permission can update other Super Admins
    if (!canExecuteDirectly && (id || formDataPayload?.id)) {
      try {
        // Check if the user has a Super Admin account
        const authResp: any = await axiosInstance.get(`/api/accounts/${id || formDataPayload.id}`);
        if (authResp?.status === 200 && authResp?.data?.data?.role === 0) {
          toast.error("Forbidden: Super Admin users can only be edited by other Super Admins or users with admin permission");
          return;
        }
      } catch (authError: any) {
        // If account doesn't exist or error, continue (user might not have account yet)
        // Only block if we confirmed they're Super Admin
        if (authError?.response?.data?.data?.role === 0) {
          toast.error("Forbidden: Super Admin users can only be edited by other Super Admins or users with admin permission");
          return;
        }
      }
    }
    
    try {
      // Use the id prop if available, otherwise try to get it from formData state
      const userId = id || formData?.id;
      if (!userId) {
        toast.error("User ID is required");
        return;
      }
      
      // When approving a request, execute directly without invocation
      // Otherwise, create a request for approval (unless user can execute directly)
      const url = requestId 
        ? `/api/user/${userId}` 
        : `/api/user/${userId}?invocation=${canExecuteDirectly ? 0 : role}`;
      
      const resp: any = await axiosInstance.put(
        url,
        formDataPayload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (resp.status === 200) {
        if (requestId) {
          handleUpdateStatus(requestId);
        }
        if (requestId) {
          toast.success("Request approved and user updated successfully");
        } else if (canExecuteDirectly) {
          toast.success("User details updated successfully");
        } else {
          toast.success("Edit user request has been sent");
        }
        onClose();
        getCompnay();
        setFormData(null);
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error?.response?.data?.message || error?.response?.data?.body || error?.message;
      if (errorMessage && errorMessage.includes("Super Admin")) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage || "Error updating user");
      }
    }
  };

  const handleRejectRequest = async () => {
    if (!requestId) return;
    try {
      const resp: any = await axiosInstance.put(`/api/request/${requestId}`, {
        status: "2",
      });
      if (resp.status === 200) {
        toast.success("Request rejected");
        onClose();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.body || "Failed to reject request");
    }
  };

  const handleUpdateStatus = async (id: any) => {
    try {
      const resp: any = await axiosInstance.put(`/api/request/${id}`, {
        status: "1",
        requester_id: Number(formData?.requester),
        checker_id: Number(role),
        request_id: id,
      });
      if (resp.status === 200) {
        getCompnay();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error?.response?.data?.body);
    }
  };

  useEffect(() => {
    getCompnay();
    getZones();
    fetchCountries();
    fetchMemberTypes();
  }, []);

  // Handle location cascading
  useEffect(() => {
    if (selectedCountryId) {
      fetchStates(selectedCountryId);
    } else {
      setStates([]);
      setDistricts([]);
      setTaluks([]);
      setCities([]);
    }
  }, [selectedCountryId]);

  useEffect(() => {
    if (selectedStateId) {
      fetchDistricts(selectedStateId);
    } else {
      setDistricts([]);
      setTaluks([]);
      setCities([]);
    }
  }, [selectedStateId]);

  useEffect(() => {
    if (selectedDistrictId) {
      fetchTaluks(selectedDistrictId);
    } else {
      setTaluks([]);
      setCities([]);
    }
  }, [selectedDistrictId]);

  useEffect(() => {
    if (useFilter) {
      if (selectedTalukId) {
        fetchCities(selectedTalukId);
      } else {
        setCities([]);
      }
    } else {
      fetchAllCities();
    }
  }, [selectedTalukId, useFilter]);

  useEffect(() => {
    if (id) {
      // When approving a request, do not fetch member — use requestData so requested changes are shown
      if (requestId) {
        return;
      }
      // Reset location state when loading new user
      setSelectedCountryId(null);
      setSelectedStateId(null);
      setSelectedDistrictId(null);
      setSelectedTalukId(null);
      setSelectedCityId(null);
      setInitialCityId(null);
      setUseFilter(false);
      setStates([]);
      setDistricts([]);
      setTaluks([]);
      fetchAllCities();
      handleGet(id);
    } else {
      // Reset location state when creating new user
      setSelectedCountryId(null);
      setSelectedStateId(null);
      setSelectedDistrictId(null);
      setSelectedTalukId(null);
      setSelectedCityId(null);
      setInitialCityId(null);
      setUseFilter(false);
      setStates([]);
      setDistricts([]);
      setTaluks([]);
      fetchAllCities();
    }
  }, [id, requestId]);

  useEffect(() => {
    if (formData && id && formData.date_of_death && !formData.is_deceased) {
      setFormData((prev: any) => ({ ...prev, is_deceased: true }));
    }
  }, [formData, id]);

  useEffect(() => {
    if (requestData) {
      // Ensure all required fields are present, including spiritual dates from approval payload
      const sanitizedData = {
        ...requestData,
        name: requestData.name || "",
        phone: requestData.phone || "",
        address: requestData.address || "",
        gender: requestData.gender || "male",
        date_of_birth: requestData.date_of_birth || "",
        member_type: requestData.member_type || "",
        baptism_date: requestData.baptism_date ?? "",
        first_communion_date: requestData.first_communion_date ?? "",
      };
      
      setFormData(sanitizedData);
      setInitialFormData(sanitizedData);
      // APARTMENT FUNCTIONALITY DISABLED
      // setInitialMultiple(requestData?.apartment_ids);
      // setMultiple(requestData?.apartment_ids);
      let imgPath = requestData.img_path || requestData.image_path || "";
      if (imgPath.startsWith("/")) imgPath = imgPath.slice(1);
      if (imgPath.startsWith("\\")) imgPath = imgPath.slice(1);
      setPreviewUrl(imgPath ? `http://localhost:3070/${imgPath}` : "");
      setAvatarError(false);
      
      // Set initial city_id for change detection
      if (requestData.city_id) {
        setSelectedCityId(requestData.city_id);
        setInitialCityId(requestData.city_id);
      } else {
        setSelectedCityId(null);
        setInitialCityId(null);
      }
      console.log("Request data loaded:", sanitizedData);
    }
  }, [requestData]);

  const today: string = new Date().toISOString().split("T")[0];
  const isApprovalMode = !!requestId;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 transition-opacity duration-300"
        onClick={onClose}
      />
      {/* Side Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto z-50 animate-in slide-in-from-right">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 dark:border-opacity-80 px-6 py-4 flex items-center justify-between z-10 shadow-sm">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {requestId ? "Review request" : (id || formData?.id) ? "Edit Member" : "Add Member"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            aria-label="Close"
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>
        {isApprovalMode && (
          <p className="px-6 pb-2 text-sm text-gray-600 dark:text-gray-400">
            Review the requested changes below, then Approve or Reject.
          </p>
        )}
        <div className="p-6">
        <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <fieldset className="space-y-6 border-0 p-0 m-0" disabled={isApprovalMode}>
          <div className="flex flex-col items-center bg-gray-50 dark:bg-gray-700/50 rounded-lg p-6 border border-gray-200 dark:border-gray-700 dark:border-opacity-80">
            <div className="relative mb-3">
              {!avatarError && previewUrl ? (
                <img
                  src={previewUrl}
                  alt={formData?.name || "Uploaded Avatar"}
                  className="w-30 h-30 rounded-full object-cover"
                  style={{ width: '120px', height: '120px' }}
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div
                  className="w-30 h-30 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold"
                  style={{ width: '120px', height: '120px', fontSize: '2.5rem' }}
                >
                  {formData?.name
                    ? formData.name
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()
                    : "U"}
                </div>
              )}
            </div>
            {!isApprovalMode && (
              <label className="btn-primary mb-4 px-4 py-2 cursor-pointer inline-flex items-center gap-2">
                <i className="fa-solid fa-upload"></i>
                Upload Image
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            )}
            <div className="flex flex-wrap items-center gap-4 mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(formData?.is_leader)}
                  onChange={(e) => handleChage("is_leader", e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 accent-gray-900 dark:accent-gray-400"
                />
                <span className="text-gray-700 dark:text-gray-300 font-medium">Is Leader</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={Boolean(formData?.is_family_head)}
                  onChange={(e) => handleChage("is_family_head", e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 accent-gray-900 dark:accent-gray-400"
                />
                <span className="text-gray-700 dark:text-gray-300 font-medium">Family Head</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData?.name || ""}
              onChange={(e: any) => handleChage("name", e?.target.value)}
              className="dark-input w-full px-4 py-2.5"
            />
            {formData?.name?.length > 25 && (
              <p className="mt-1 text-xs text-red-500">
                Character limit exceeded (25 max)
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <DatePicker
                value={formData?.date_of_birth ? String(formData.date_of_birth) : ""}
                onChange={(date) => handleChage("date_of_birth", date)}
                max={today}
                min="1900-01-01"
                placeholder="Select date of birth"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                value={formData?.gender ? formData?.gender : 0}
                onChange={(e) => handleChage("gender", e?.target?.value)}
                className="dark-input w-full px-4 py-2.5"
              >
                <option value={0}>Select Gender</option>
                {["male", "female"].map((item: any) => (
                  <option key={item} value={item}>
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData?.member_type ? formData?.member_type : 0}
                onChange={(e) => handleChage("member_type", e?.target?.value)}
                className="dark-input w-full px-4 py-2.5"
              >
                <option value={0}>Select Type</option>
                {memberTypes.map((item: any) => (
                  <option key={item?.value} value={item?.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData?.phone || ""}
              onChange={(e: any) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  handleChage("phone", val);
                }
              }}
              className="dark-input w-full px-4 py-2.5"
            />
            {formData?.phone?.length > 10 && (
              <p className="mt-1 text-xs text-red-500">
                Character limit exceeded (10 max)
              </p>
            )}
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData?.is_whatsapp_enabled ?? false}
                onChange={(e) =>
                  handleChage("is_whatsapp_enabled", e.target.checked)
                }
                className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 accent-gray-900 dark:accent-gray-400"
              />
              <span className="text-gray-700 dark:text-gray-300 font-medium">Whatsapp Enabled</span>
            </label>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Date of Baptism
              </label>
              <DatePicker
                value={formData?.baptism_date || ""}
                onChange={(date) => handleChage("baptism_date", date)}
                max={today}
                min="1900-01-01"
                placeholder="Select baptism date"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Date of First Communion
              </label>
              <DatePicker
                value={formData?.first_communion_date || ""}
                onChange={(date) => handleChage("first_communion_date", date)}
                max={today}
                min="1900-01-01"
                placeholder="Select first communion date"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Zone</label>
            <select
              value={formData?.zone_id || ""}
              onChange={(e: any) => handleChage("zone_id", e?.target.value)}
              className="dark-input w-full px-4 py-2.5"
            >
              <option value="">Select Zone</option>
              {zones.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.zone_name}
                </option>
              ))}
            </select>
          </div>

          {/* Location Selection */}
          <div className="text-lg font-semibold text-gray-700 dark:text-gray-300 mt-6 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
            Location
          </div>
          
          {/* Filter Checkbox */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={useFilter}
                onChange={(e) => {
                  setUseFilter(e.target.checked);
                  if (!e.target.checked) {
                    // Reset filters when unchecked
                    setSelectedCountryId(null);
                    setSelectedStateId(null);
                    setSelectedDistrictId(null);
                    setSelectedTalukId(null);
                    setStates([]);
                    setDistricts([]);
                    setTaluks([]);
                  }
                }}
                className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 accent-gray-900 dark:accent-gray-400"
              />
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                Filter by location to find locality
              </span>
            </label>
          </div>

          {/* Filter Dropdowns - Only shown when checkbox is checked */}
          {useFilter && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Country
                </label>
                <select
                  value={selectedCountryId || ""}
                  onChange={(e: any) => {
                    const countryId = e.target.value ? Number(e.target.value) : null;
                    setSelectedCountryId(countryId);
                    setSelectedStateId(null);
                    setSelectedDistrictId(null);
                    setSelectedTalukId(null);
                    setSelectedCityId(null);
                  }}
                  className="dark-input w-full px-4 py-2.5"
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  State
                </label>
                <select
                  value={selectedStateId || ""}
                  onChange={(e: any) => {
                    const stateId = e.target.value ? Number(e.target.value) : null;
                    setSelectedStateId(stateId);
                    setSelectedDistrictId(null);
                    setSelectedTalukId(null);
                    setSelectedCityId(null);
                  }}
                  disabled={!selectedCountryId}
                  className="dark-input w-full px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  District
                </label>
                <select
                  value={selectedDistrictId || ""}
                  onChange={(e: any) => {
                    const districtId = e.target.value ? Number(e.target.value) : null;
                    setSelectedDistrictId(districtId);
                    setSelectedTalukId(null);
                    setSelectedCityId(null);
                  }}
                  disabled={!selectedStateId}
                  className="dark-input w-full px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select District</option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Taluk
                </label>
                <select
                  value={selectedTalukId || ""}
                  onChange={(e: any) => {
                    const talukId = e.target.value ? Number(e.target.value) : null;
                    setSelectedTalukId(talukId);
                    setSelectedCityId(null);
                  }}
                  disabled={!selectedDistrictId}
                  className="dark-input w-full px-4 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select Taluk</option>
                  {taluks.map((taluk) => (
                    <option key={taluk.id} value={taluk.id}>
                      {taluk.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Locality Dropdown - Always Visible */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Locality <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <select
              value={selectedCityId || ""}
              onChange={(e: any) => {
                const cityId = e.target.value ? Number(e.target.value) : null;
                setSelectedCityId(cityId);
              }}
              className="dark-input w-full px-4 py-2.5"
            >
              <option value="">Select Locality</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
          {/*}
          <div>
            <FormLabel>Company</FormLabel>
            <Select labelId="demo-simple-select-label" size="small" fullWidth multiple id="demo-simple-select" value={Array.isArray(multiple) ? multiple : []} onChange={handleChangeCompany} renderValue={(selected) => (<div style={{ display: "flex", flexDirection: "column" }}>{(selected || []).map((value) => { const selectedItem = companies.find((item: any) => item.id === value); return (<span key={value}>{selectedItem?.name || value}</span>); })}</div>)}>
              {companies?.map((item: any, index: any) => (
                <MenuItem key={index} value={item?.id}>{item.name}</MenuItem>
              ))}
            </Select>
          </div>{*/}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Address <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={4}
              value={formData?.address || ""}
              onChange={(e: any) => handleChage("address", e?.target.value)}
              className="dark-input w-full px-4 py-2.5"
            />
            {formData?.address?.length >= 250 && (
              <p className="mt-1 text-xs text-red-500">
                Character limit exceeded (250 max)
              </p>
            )}
          </div>
          {!id && (
            <>
              <div className="text-lg font-semibold text-gray-700 dark:text-gray-300 mt-6 mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                Vehicle Details
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Register No</label>
                  <input
                    type="text"
                    value={formData?.reg_no || ""}
                    onChange={(e: any) =>
                      handleChage("reg_no", e?.target.value)
                    }
                    className="dark-input w-full px-4 py-2.5"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Vehicle Type</label>
                  <select
                    value={formData?.type ? formData?.type : 0}
                    onChange={(e: any) => handleChage("type", e?.target.value)}
                    className="dark-input w-full px-4 py-2.5"
                  >
                    <option value={0}>Select Vehicle Type</option>
                    {vehicleTypes.map((item: any) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}
          {/* Member Deceased Section - Only visible to Super Admin, users with critical_data permission, or users with admin permission */}
          {id && (Number(role) === 0 || hasPermissionSync("critical_data") || hasPermissionSync("admin")) && (
            <div className="flex flex-col mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
                Member Deceased
              </div>
              <div className="flex flex-col">
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={!!formData?.is_deceased}
                    onChange={(e) => {
                      handleChage("is_deceased", e.target.checked);
                      if (!e.target.checked) handleChage("date_of_death", "");
                    }}
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 dark:text-blue-500 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 accent-gray-900 dark:accent-gray-400"
                  />
                  <span className="text-gray-700 dark:text-gray-300 font-medium">Member deceased</span>
                </label>
                {!!formData?.is_deceased && (
                  <>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 mt-2">Date of Death</label>
                    <DatePicker
                      value={formData?.date_of_death || ""}
                      onChange={(date) => handleChage("date_of_death", date)}
                      max={today}
                      min={formData?.date_of_birth || "1900-01-01"}
                      placeholder="Select date of death"
                    />
                  </>
                )}
              </div>
            </div>
          )}
          </fieldset>
          <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 dark:border-opacity-80 -mx-6 px-6 py-4 mt-8 flex justify-end gap-3 shadow-lg">
            {requestId ? (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-primary px-6 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleRejectRequest}
                  className="px-6 py-2.5 rounded-lg font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn-primary px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white"
                >
                  Approve
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-primary px-6 py-2.5"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn-primary px-6 py-2.5"
                >
                  {(id || formData?.id) ? "Update Member" : "Add Member"}
                </button>
              </>
            )}
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default UserCreateForm;
